import { ViewChild, Component, OnInit, OnDestroy } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { Geometry, MOUSE_EVENT_TYPE, Marker } from '@realpage-da/ng-map';

import { MarketDetail } from '~/core/model';
import { MarketService, AnalyticsService } from '~/core/services';
import { ViewComponent } from './view.component';
import { MarketMarkerComponent, MarketTooltipComponent } from '~/features/market/components/map';
import { MarketLegendComponent } from '~/features/market/components/map';
import { MarketTableComponent } from '~/features/market/components/tables';
import { SIZES } from '~/shared/configs';

import { VIEW_LEVELS } from '../constants';
import { keyBy } from 'lodash'
import { Subject } from 'rxjs/Subject';
import { FilterFunction } from '~/shared/components/filters/components/controls';

@Component({
  selector: 'idap-market-view',
  templateUrl: './market-view.component.html',
  styleUrls: ['./market-view.component.scss']
})
export class MarketViewComponent extends ViewComponent implements OnInit, OnDestroy {
  static viewLevel = VIEW_LEVELS.MARKET;

  private markets: Observable<MarketDetail[]>;
  @ViewChild(MarketTableComponent) private table: MarketTableComponent;

  // the passed value here is based on the total
  // characters width we have defined for all the
  // columns in the market level grid
  maxWidth: number = SIZES.characterWidth(139);
  halfWidth: number = SIZES.characterWidth(77.5);

  private filteredMarkets: Observable<MarketDetail[]>;
  onFilterChanged: Subject<FilterFunction<any>> = new Subject();
  markers: Observable<Marker[]>;

  constructor(
    private marketService: MarketService,
    public analytics: AnalyticsService) {
    super();
    this.markets = this.marketService.getMarketMetrics();
  }

  getSize(size: 'open' | 'closed' | 'full'): number {
    if (!this.table || size !== 'open') return 0;
  }

  ngAfterViewInit() {
    this.map.map.setPolygons([]);
    this.map.map.addLegend(MarketLegendComponent);
    const mouseEvent = this.map.map.onMarkerSelected
      .takeUntil(this.onDestroy)
      .share();

    mouseEvent
      .filter(e => e.event === MOUSE_EVENT_TYPE.CLICK)
      .takeUntil(this.onDestroy)
      .subscribe((clickEvent) => {
        this.parent.updateLocationState(`/main/${clickEvent.marker.data.primaryState.toLowerCase()}/${clickEvent.marker.data.urlSlug}`);
        this.map.map.removeTooltip();
        this.analytics.emitEvent('click', {
          event_category: 'market',
          event_label: 'map-marker-click',
          value: clickEvent.marker.data.id
        });
      });

    mouseEvent
      .filter(e => e.event === MOUSE_EVENT_TYPE.MOUSEENTER)
      .takeUntil(this.onDestroy)
      .subscribe((mouseHoverEvent) => {
        this.map.map.addTooltip(mouseHoverEvent.marker)
      });

    mouseEvent
      .filter(e => e.event === MOUSE_EVENT_TYPE.MOUSELEAVE)
      .takeUntil(this.onDestroy)
      .subscribe(mouseOutEvent => this.map.map.removeTooltip());
  }

  marketSelected(data) {
    const market = this.map.map.findFeature(data.id);

    if (market) {
      this.parent.updateLocationState(`/main/${market.data.primaryState.toLowerCase()}/${market.data.urlSlug}`);
    }

    this.analytics.emitEvent('click', {
      event_category: 'market',
      event_label: 'grid-row-click',
      value: data.id
    });
  }

  ngOnInit() {
    const markers = this.marketService.getMarketBounds()
      .map(boundaries => boundaries
        .map(boundary => ({
          id: boundary.properties.id,
          geometry: new Geometry.Point(+boundary.properties.latitude, +boundary.properties.longitude),
          data: boundary.properties,
          options: {
            icon: {
              component: MarketMarkerComponent
            },

            tooltip: MarketTooltipComponent
          }
        })))
      .takeUntil(this.onDestroy);

    this.filteredMarkets = Observable.combineLatest(this.markets, this.onFilterChanged)
      .map(([markets, filterFn]) => markets.filter(m => filterFn(m)));

    this.markers = Observable.combineLatest(this.filteredMarkets, markers)
      .map(([markets, markers]) => {
        const marketMap = keyBy(markets, m => m.id);
        return markers.filter(f => !!marketMap[f.id]);
      })
      .shareReplay(1);

    this.markers
      .takeUntil(this.onDestroy)
      .subscribe(markers => this.map.map.setMarkers(markers));
  }

  ngOnDestroy() {
    this.map.map.removeLegend();
    this.onFilterChanged.complete();
    return super.ngOnDestroy();
  }
}



// WEBPACK FOOTER //
// ./src/app/main/views/market-view.component.ts